/**
 * Runs `worker` for every item, but never more than `limit` at the same time.
 * Decoding hundreds of phone photos concurrently freezes or kills mobile
 * browsers, so everything that touches image data goes through this.
 *
 * Resolves with the results in input order once every item has been handled
 * (a rejected worker does not stop the others, it is returned as `{error}`).
 */
export function mapWithConcurrency(items, limit, worker) {
    const list = Array.from(items);
    const results = new Array(list.length);
    let next = 0;

    const run = async () => {
        while(next < list.length) {
            const index = next++;

            try {
                results[index] = await worker(list[index], index);
            } catch(error) {
                results[index] = { error };
            }
        }
    };

    const workers = [];
    for(let i = 0; i < Math.max(1, Math.min(limit, list.length)); i++) {
        workers.push(run());
    }

    return Promise.all(workers).then(() => results);
}

/**
 * Rough device class detection, used to pick concurrency limits. Phones and
 * tablets get less parallel image work than desktops.
 */
export function isMobileDevice() {
    const userAgent = navigator.userAgent || '';
    const touchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1; // iPadOS pretends to be a Mac

    return /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) || touchMac;
}
